import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { IAnalyticsEvent } from 'gatsby-plugin-purina-analytics/types';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import appStoreBadgeBlack from 'src/assets/media/images/app-appstore-badge-black.svg';
import playStoreBadge from 'src/assets/media/images/app-playstore-badge.png';

import { CMS_THEME } from 'src/common/enums';
import { ImageLink } from 'src/components/Link';
import { useIsMobileDevice } from 'src/hooks/useIsMobileDevice';
import { themeVariant } from 'src/theme/basic-styles';

export interface AppLinkBundleProps {
  analyticsEvent?: Partial<IAnalyticsEvent>;
  appStoreLink?: LinkProps;
  className?: string;
  badgeAlignment?: 'start' | 'center' | 'end';
  cmsTheme?: CMS_THEME;
  body?: string;
  bodyClassName?: string;
  playStoreLink?: LinkProps;
  qrCodeLink?: LinkProps;
}

const bodyTheme = themeVariant({ isOnlyText: true });

const bodyStylesVariants = pdsTv({
  base: 'pds-mb-3',
  variants: {
    theme: {
      light: bodyTheme.light,
      dark: bodyTheme.dark,
      neutral: bodyTheme.neutral,
    },
  },
});

const badgeWrapperStylesVariants = pdsTv({
  base: 'pds-flex pds-flex-row pds-flex-wrap pds-items-start pds-justify-center pds-gap-3.5',
  variants: {
    qrCodeLink: {
      true: 'sm:pds-flex-col',
    },
    alignment: {
      start: 'pds-items-start pds-justify-start',
      center: 'pds-items-center pds-justify-center',
      end: 'pds-items-end pds-justify-end',
    },
  },
});

function handleGA(
  link: 'app store' | 'play store',
  analyticsEvent: AppLinkBundleProps['analyticsEvent'],
  appStoreLink: AppLinkBundleProps['appStoreLink'],
  playStoreLink: AppLinkBundleProps['playStoreLink'],
): void {
  const hasTwoBadges = appStoreLink?.url && playStoreLink?.url;

  const componentId = hasTwoBadges
    ? 'primary_cta: app store, secondary_cta: play store'
    : `primary_cta: ${link}`;

  sendIt({
    event: analyticsEvent?.event ?? 'module_interaction',
    eventParams: {
      component_id: componentId,
      ...analyticsEvent?.eventParams,
    },
  });
}

const outerWrapper = pdsTv({
  base: 'pds-flex pds-items-center pds-justify-center pds-gap-4.5 sm:pds-justify-start',
  variants: {
    alignment: {
      start: 'pds-items-start pds-justify-start',
      center: 'pds-items-center pds-justify-center',
      end: 'pds-items-end pds-justify-end',
    },
  },
});

const AppLinkBundle: React.FC<AppLinkBundleProps> = ({
  analyticsEvent,
  appStoreLink,
  bodyClassName,
  badgeAlignment,
  className,
  cmsTheme = CMS_THEME.LIGHT,
  body,
  playStoreLink,
  qrCodeLink,
}) => {
  const { t } = useTranslation();
  const isMobileDevice = useIsMobileDevice();

  const bodyStyles = bodyStylesVariants({ theme: cmsTheme, class: bodyClassName });
  const badgeWrapperStyles = badgeWrapperStylesVariants({
    qrCodeLink: Boolean(qrCodeLink),
    alignment: badgeAlignment,
  });

  const outerWrapperStyles = outerWrapper({
    alignment: badgeAlignment,
  });

  const showQrCode = qrCodeLink?.url && !isMobileDevice.current;
  const showQrBody = body && !isMobileDevice.current;

  return (
    <div className={className}>
      {showQrBody && <p className={bodyStyles}>{body}</p>}

      <div className={outerWrapperStyles}>
        {showQrCode && (
          <div className="pds-h-[124px] pds-w-[124px] pds-bg-white pds-p-[6px]">
            <QRCodeSVG
              value={qrCodeLink.url}
              size={112}
              marginSize={0}
              level="M"
              title={qrCodeLink.options?.attributes?.ariaLabel}
            />
          </div>
        )}

        <div className={badgeWrapperStyles}>
          {appStoreLink?.url && (
            <ImageLink
              to={appStoreLink.url}
              cmsTheme={cmsTheme}
              {...appStoreLink.options?.attributes}
              aria-label={appStoreLink.options?.attributes?.ariaLabel ?? t('Apple App Store')}
              onClick={() => handleGA('app store', analyticsEvent, appStoreLink, playStoreLink)}
            >
              <img
                className="pds-h-[40px] pds-min-h-[40px]"
                src={appStoreBadgeBlack.toString()}
                alt=""
                aria-hidden="true"
              />
            </ImageLink>
          )}

          {playStoreLink?.url && (
            <ImageLink
              to={playStoreLink.url}
              cmsTheme={cmsTheme}
              {...playStoreLink.options?.attributes}
              aria-label={playStoreLink.options?.attributes?.ariaLabel ?? t('Google Play Store')}
              onClick={() => handleGA('play store', analyticsEvent, appStoreLink, playStoreLink)}
            >
              <img
                className="pds-h-[40px] pds-min-h-[0.3in]"
                src={playStoreBadge}
                alt=""
                aria-hidden="true"
              />
            </ImageLink>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppLinkBundle;
